/**
 * The id a pay-profile row is keyed by, and whether a routed id can be used.
 *
 * WHY THIS EXISTS
 * ---------------
 * `GET /pay-profiles` selects `epp.*` from `employee_pay_profiles`, whose column
 * is `employee_id`. Three call sites read `user_id` / `worker_id` instead, so
 * the value was `undefined` on every row and the link built from it read
 * `/payroll/workers/undefined/pay-profile`. Nothing failed on the list itself —
 * `worker_name` IS returned, so the row rendered correctly and only the link was
 * wrong, which is why it survived.
 *
 * `GET /payroll/reports/pay-summary` maps its rows to `user_id` in BOTH branches
 * (tickets and timesheets), and the summary screen read `worker_id`. Same shape,
 * different spelling — hence one helper rather than three fixes.
 *
 * Reads tolerate every spelling; see CLAUDE.md on the serialization asymmetry.
 */
export function workerIdOf(row: any): string | null {
  const id = row?.employee_id ?? row?.employeeId ?? row?.user_id ?? row?.userId ?? row?.worker_id ?? row?.workerId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * Is a routed :userId something the API can actually look up?
 *
 * Not decoration. `/pay-profiles/users/:userId` has no `requireUuid`, so a
 * non-uuid reaches Postgres and comes back 22P02 → 400 with copy that blames
 * the values the caller sent — for a request the caller never typed. Refusing
 * to send it is the frontend's half; the API guard is a separate ticket.
 *
 * Deliberately shape-only: proving an id EXISTS is the API's job, and a
 * frontend that tried would be a second answer to that question.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isLookupableId(id: string | undefined | null): boolean {
  return typeof id === 'string' && UUID_SHAPE.test(id);
}
